// Every element that should be re-rendered after a cart mutation marks itself
// with `cart-section-id`. Resolved per request rather than once at import time,
// so this module no longer throws on pages with no cart markup and picks up
// sections added to the DOM later.
const getCartSectionIds = () =>
  [...document.querySelectorAll("[cart-section-id]")].map((el) =>
    el.getAttribute("cart-section-id"),
  );

/**
 * Change an item in the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.changeCart = (change, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  const sections =
    "sections" in options
      ? [...getCartSectionIds(), ...options.sections]
      : getCartSectionIds();

  fetch(`${window.routes.cart_change_url}.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...change, sections: sections.join() }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422) {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};

/**
 * Remove an item from the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.updateCart = (updates, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  const sections =
    "sections" in options
      ? [...getCartSectionIds(), ...options.sections]
      : getCartSectionIds();

  fetch(`${window.routes.cart_update_url}.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...updates, sections: sections.join() }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422) {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};

/**
 * Add an item to cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.addToCart = (items, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }
  if (!Array.isArray(items)) {
    items = [items];
  }
  const sections =
    "sections" in options
      ? [...getCartSectionIds(), ...options.sections]
      : getCartSectionIds();

  const data = {
    items: [...items],
    sections: sections.join(),
  };

  fetch(`${window.routes.cart_add_url}.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      return response.json().then((errorDetails) => {
        return Promise.reject(errorDetails);
      });
    })
    .then((response) => {
      const sections = response.sections;

      // onAfterAdd waits for the re-render, so a mini cart it opens already
      // shows the new line. The add has succeeded by now, so a failed refresh
      // still reaches onAfterAdd rather than onError.
      return fetch(`${window.routes.cart_url}.js`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((response) => {
          const updateCart = new CustomEvent("updateCart", {
            detail: {
              cart: { ...response, sections },
              data: options.data,
            },
          });
          document.dispatchEvent(updateCart);
        })
        .catch((error) => console.error("Cart refresh failed:", error))
        .then(() => {
          if (options?.onAfterAdd) {
            options.onAfterAdd(response);
          }
        });
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};
